import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import { getLandingPageSchema } from '../utils/structuredData';
import { listaModulos } from '../data/modulos';
import { planos, beneficiosComuns, CICLOS_COBRANCA, opcoesCiclo } from '../data/planos';
import { competitors } from '../data/competitors';
import UrgencyTimer from './UrgencyTimer';
import WhatsAppFloat from './WhatsAppFloat';
import OptimizedImage from './OptimizedImage';
import ProductGallery from './ProductGallery';
import './LandingPage.css';
import { trackCTA } from '../utils/analytics';

// Número do WhatsApp (formato: 5511999999999 - substitua pelo número real)
// Exemplo: 5511987654321 (11 = DDD, 987654321 = número)
const WHATSAPP_NUMBER = '5543996782197';

// Função para gerar URL do WhatsApp com mensagem personalizada
const getWhatsAppUrl = (message = '') => {
  const encodedMessage = encodeURIComponent(message);
  return `https://wa.me/${WHATSAPP_NUMBER}${message ? `?text=${encodedMessage}` : ''}`;
};

const ADS_EVENTS = {
  WHATSAPP_HEADER: 'ads_conv_lp_whatsapp_header',
  WHATSAPP_MOBILE_MENU: 'ads_conv_lp_whatsapp_mobile_menu',
  WHATSAPP_INTEGRACOES: 'ads_conv_lp_whatsapp_integracoes',
  WHATSAPP_CTA_FINAL: 'ads_conv_lp_whatsapp_cta_final',
  WHATSAPP_FOOTER_CONTATO: 'ads_conv_lp_whatsapp_footer_contato',
  DOWNLOAD_CTA_FINAL: 'ads_conv_lp_download_cta_final',
  DOWNLOAD_FOOTER: 'ads_conv_lp_download_footer',
};

const LandingPage = () => {
  void Link;
  void listaModulos;
  void competitors;
  void UrgencyTimer;
  void ProductGallery;
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [cicloCobranca, setCicloCobranca] = useState(CICLOS_COBRANCA.MENSAL);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Previne scroll do body quando menu mobile está aberto
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Função helper para lidar com cliques no WhatsApp com rastreamento de conversão
  const sendAdsEvent = (eventName, callback) => {
    if (window.gtag) {
      window.gtag('event', eventName, {
        'event_callback': callback,
        'event_timeout': 2000,
      });
    } else {
      callback();
    }
  };

  const handleWhatsAppClick = (e, message = '', eventName) => {
    e.preventDefault();
    const whatsappUrl = getWhatsAppUrl(message);
    
    if (window.gtagSendEvent) {
      window.gtagSendEvent(whatsappUrl, eventName);
    } else {
      sendAdsEvent(eventName, () => {
        window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
      });
    }
  };

  // Função helper para lidar com cliques em links de download com rastreamento de conversão
  const handleDownloadClick = (e, url, eventName) => {
    e.preventDefault();
    
    if (window.gtagSendEvent) {
      window.gtagSendEvent(url, eventName);
    } else if (window.gtag) {
      sendAdsEvent(eventName, () => {
        window.location.href = url;
      });
    } else {
      window.location.href = url;
    }
  };

  const structuredData = getLandingPageSchema('https://www.veritux.com.br');

  return (
    <div className="landing-page">
      <SEO
        title="Veritux ERP - Gestão integrada para pequenas e médias empresas | Financeiro, estoque e fiscal"
        description="ERP para Windows, instalado no seu computador, para pequenas e médias empresas: vendas, estoque, financeiro e obrigações fiscais em um fluxo único. Teste grátis por 15 dias, sem cartão."
        keywords="ERP, sistema ERP, gestão empresarial, sistema de gestão, controle de estoque, gestão financeira, produção de vestuário, ERP Brasil, software de gestão, gestão de vendas, NF-e, automação empresarial, ERP para pequenas e médias empresas, sistema de gestão integrado, ERP para Windows, ERP desktop, gestão de estoque, controle financeiro"
        url="https://www.veritux.com.br"
        structuredData={structuredData}
      />
      {/* Header/Navbar */}
      <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <img src="/Logo.ico" alt="Veritux ERP" className="logo-img" />
              <span className="logo-text">Veritux ERP</span>
            </div>
            <nav className="nav" aria-label="Navegação principal">
              <a href="/funcionalidades" aria-label="Ir para seção de Funcionalidades">Funcionalidades</a>
              <a href="#beneficios" aria-label="Ir para seção de Benefícios">Benefícios</a>
              <a href="#como-funciona" aria-label="Ir para seção Como Funciona">Como Funciona</a>
              <a href="#precos" aria-label="Ir para seção de Preços">Preços</a>
            </nav>
            <div className="header-cta">
              <a
                href="#demo"
                className="btn btn-primary"
                aria-label="Ir para cadastro do teste grátis"
                onClick={() => trackCTA('criar_conta_gratis', 'header')}
              >
                Testar grátis por 15 dias
              </a>
              <a 
                href={getWhatsAppUrl('Olá! Gostaria de saber mais sobre o Veritux ERP.')} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn btn-outline" 
                aria-label="Fale conosco via WhatsApp"
                onClick={(e) => {
                  trackCTA('whatsapp', 'header');
                  handleWhatsAppClick(e, 'Olá! Gostaria de saber mais sobre o Veritux ERP.', ADS_EVENTS.WHATSAPP_HEADER);
                }}
              >
                Falar no WhatsApp
              </a>
            </div>
            <button 
              className="mobile-menu-toggle"
              onClick={toggleMobileMenu}
              aria-label="Abrir menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span className={`hamburger ${isMobileMenuOpen ? 'active' : ''}`}>
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'active' : ''}`} onClick={closeMobileMenu}></div>
      <nav className={`mobile-menu ${isMobileMenuOpen ? 'active' : ''}`} aria-label="Menu mobile">
        <div className="mobile-menu-header">
          <div className="logo">
            <img src="/Logo.ico" alt="Veritux ERP" className="logo-img" />
            <span className="logo-text">Veritux ERP</span>
          </div>
          <button 
            className="mobile-menu-close"
            onClick={closeMobileMenu}
            aria-label="Fechar menu"
          >
            <span>×</span>
          </button>
        </div>
        <div className="mobile-menu-content">
          <a href="/funcionalidades" onClick={closeMobileMenu} className="mobile-menu-link">Funcionalidades</a>
          <a href="#beneficios" onClick={closeMobileMenu} className="mobile-menu-link">Benefícios</a>
          <a href="#como-funciona" onClick={closeMobileMenu} className="mobile-menu-link">Como Funciona</a>
          <a href="#precos" onClick={closeMobileMenu} className="mobile-menu-link">Preços</a>
          <div className="mobile-menu-buttons">
            <a 
              href="#demo" 
              className="btn btn-primary" 
              onClick={() => {
                closeMobileMenu();
                trackCTA('criar_conta_gratis', 'mobile_menu');
              }}
            >
              Testar grátis por 15 dias
            </a>
            <a 
              href={getWhatsAppUrl('Olá! Gostaria de saber mais sobre o Veritux ERP.')} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn btn-outline" 
              onClick={(e) => {
                closeMobileMenu();
                trackCTA('whatsapp', 'mobile_menu');
                handleWhatsAppClick(e, 'Olá! Gostaria de saber mais sobre o Veritux ERP.', ADS_EVENTS.WHATSAPP_MOBILE_MENU);
              }}
            >
              Falar no WhatsApp
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero" itemScope itemType="https://schema.org/SoftwareApplication">
        <div className="container">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title" itemProp="name">
                ERP para pequenas e médias empresas: <span className="highlight">financeiro</span>,{' '}
                <span className="highlight">estoque</span> e <span className="highlight">fiscal</span> no mesmo lugar
              </h1>
              <p className="hero-subtitle">
                Menos planilhas e retrabalho. Mais clareza para decidir com números da operação.
              </p>
              <p className="hero-description">
                O Veritux integra vendas, compras, estoque e obrigações fiscais em um fluxo único.
                Os resultados variam conforme o processo de cada empresa; nosso foco é dar visibilidade
                e controle no dia a dia.
              </p>
              <div className="hero-cta">
                <a
                  href="#demo"
                  className="btn btn-primary btn-large"
                  onClick={() => trackCTA('criar_conta_gratis', 'hero')}
                >
                  Testar grátis por 15 dias
                </a>
                <a
                  href="#video"
                  className="btn btn-secondary btn-large"
                  onClick={() => trackCTA('ver_video_demo', 'hero')}
                >
                  Ver demonstração em vídeo
                </a>
              </div>
              <div className="hero-trust">
                <p className="trust-text">✓ Sem cartão de crédito</p>
                <p className="trust-text">✓ Teste de 15 dias</p>
                <p className="trust-text">✓ Suporte completo</p>
              </div>
            </div>
            <div className="hero-visual">
              <OptimizedImage
                src="/images/product/hero/dashboard"
                alt="Dashboard do Veritux ERP mostrando controle total da empresa"
                sizes="(max-width: 768px) 100vw, 50vw"
                className="hero-dashboard-image"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Promise Section */}
      <section className="promise">
        <div className="container">
          <div className="promise-content">
            <h2 className="promise-title">
              Você nunca mais vai tomar decisões no escuro
            </h2>
            <p className="promise-text">
              Com o Veritux ERP, cada decisão é baseada em dados reais, 
              insights precisos e visibilidade total da sua operação.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="beneficios" className="benefits" aria-labelledby="beneficios-title">
        <div className="container">
          <div className="section-header">
            <h2 id="beneficios-title" className="section-title">Crescimento com clareza</h2>
            <p className="section-subtitle">
              O Veritux ERP organiza a operação para você medir, corrigir e crescer com segurança
            </p>
          </div>
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">💰</div>
              <h3 className="benefit-title">Melhor visibilidade de custos e margem</h3>
              <p className="benefit-text">
                Centralize compras, despesas e precificação para enxergar onde o dinheiro entra e sai.
                Muitas equipes reduzem desperdício quando deixam de depender só de planilhas soltas.
              </p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">✅</div>
              <h3 className="benefit-title">Menos erro operacional e retrabalho</h3>
              <p className="benefit-text">
                Automação e regras integradas reduzem digitação duplicada entre vendas, estoque e financeiro.
                Você padroniza o processo e diminui falhas manuais no fluxo.
              </p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">📈</div>
              <h3 className="benefit-title">Decisões com dados da operação</h3>
              <p className="benefit-text">
                Relatórios e dashboards ajudam a priorizar o que vende melhor, o que trava capital e onde ajustar preço.
                O ganho de lucro depende da sua estratégia; o sistema entrega a base numérica para decidir.
              </p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">⚡</div>
              <h3 className="benefit-title">Menos tempo em tarefas repetitivas</h3>
              <p className="benefit-text">
                Rotinas como faturamento, conciliação e emissão fiscal deixam de ser gargalo manual.
                Sua equipe ganha horas para vender e atender em vez de copiar dados entre sistemas.
              </p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">🔒</div>
              <h3 className="benefit-title">Fiscal e obrigações com rastreabilidade</h3>
              <p className="benefit-text">
                NF-e, NFC-e e obrigações acessórias no mesmo contexto da operação, com backup e boas práticas de segurança.
                A conformidade exige processo correto; o Veritux dá suporte à rotina e à auditoria interna.
              </p>
            </div>
            <div className="benefit-card">
              <div className="benefit-icon">🚀</div>
              <h3 className="benefit-title">Escala conforme o time e o negócio crescem</h3>
              <p className="benefit-text">
                Planos e módulos permitem evoluir sem trocar de plataforma a cada novo colaborador ou filial.
                Você mantém histórico e integração em um só lugar.
              </p>
            </div>
          </div>
          <p className="benefits-disclaimer">
            Resultados financeiros e operacionais variam conforme segmento, disciplina de uso e processo interno.
          </p>
        </div>
      </section>

      <section id="para-quem" className="audience-fit" aria-labelledby="para-quem-title">
        <div className="container">
          <div className="section-header">
            <h2 id="para-quem-title" className="section-title">Para quem o Veritux faz sentido</h2>
            <p className="section-subtitle">
              Transparência para você avaliar se encaixa no seu estágio de maturidade
            </p>
          </div>
          <div className="audience-fit-grid">
            <div className="audience-fit-column audience-fit-yes">
              <h3 className="audience-fit-heading">Boa combinação quando</h3>
              <ul className="audience-fit-list">
                <li>Sua empresa é de pequeno ou médio porte e precisa alinhar vendas, estoque e financeiro na mesma língua</li>
                <li>Quer sair de planilhas paralelas e reduzir retrabalho entre setores</li>
                <li>Precisa de NF-e e rotina fiscal alinhadas ao dia a dia comercial</li>
              </ul>
            </div>
            <div className="audience-fit-column audience-fit-no">
              <h3 className="audience-fit-heading">Talvez não seja o foco agora se</h3>
              <ul className="audience-fit-list">
                <li>Você busca só um emissor de notas sem gestão integrada</li>
                <li>Não há tempo para cadastro, treinamento básico e padronização de processo</li>
                <li>A expectativa é resultado automático sem mudança de hábito na operação</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Competitors Comparison Section 
      <section id="comparacao" className="competitors-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Por que escolher o Veritux?</h2>
            <p className="section-subtitle">
              Compare o Veritux com os principais ERPs do mercado
            </p>
          </div>
          <div className="competitors-table-wrapper">
            <table className="competitors-table">
              <thead>
                <tr>
                  <th>Recursos</th>
                  {competitors.map((competitor) => (
                    <th key={competitor.id} className={competitor.destacar ? 'highlight-column' : ''}>
                      {competitor.nome}
                      {competitor.destacar && <span className="best-badge">Melhor Escolha</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Preço</strong></td>
                  {competitors.map((competitor) => (
                    <td key={competitor.id} className={competitor.destacar ? 'highlight-cell' : ''}>
                      {competitor.preco}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td><strong>Funcionalidades</strong></td>
                  {competitors.map((competitor) => (
                    <td key={competitor.id} className={competitor.destacar ? 'highlight-cell' : ''}>
                      {competitor.funcionalidades}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td><strong>Suporte</strong></td>
                  {competitors.map((competitor) => (
                    <td key={competitor.id} className={competitor.destacar ? 'highlight-cell' : ''}>
                      {competitor.suporte}
                    </td>
                  ))}
                </tr>
                <tr>
                  <td><strong>Facilidade de Uso</strong></td>
                  {competitors.map((competitor) => (
                    <td key={competitor.id} className={competitor.destacar ? 'highlight-cell' : ''}>
                      {competitor.facilidade}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
          {competitors.find(c => c.destacar)?.diferenciais && (
            <div className="diferenciais-box">
              <h3>Diferenciais do Veritux:</h3>
              <ul className="diferenciais-list">
                {competitors.find(c => c.destacar).diferenciais.map((diferencial, index) => (
                  <li key={index}>✓ {diferencial}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>
      */}
      {/* Product Gallery Section 
      <ProductGallery />


      <section id="features" className="features" aria-labelledby="features-title">
        <div className="container">
          <div className="section-header">
            <h2 id="features-title" className="section-title">Funcionalidades Completas</h2>
            <p className="section-subtitle">
              Tudo que sua empresa precisa em um único sistema
            </p>
          </div>
          <div className="features-grid">
            {listaModulos.map((modulo) => {
              const featureImage = `/images/product/features/${modulo.id}.png`;
              return (
                <div key={modulo.id} className="feature-item">
                  {modulo.imagem && (
                    <div className="feature-image-wrapper">
                      <OptimizedImage
                        src={modulo.imagem.replace('.png', '')}
                        alt={`Screenshot do ${modulo.titulo}`}
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                        className="feature-image"
                      />
                    </div>
                  )}
                  <div className="feature-icon">{modulo.icone}</div>
                  <h3 className="feature-title">{modulo.titulo}</h3>
                  <p className="feature-text">
                    {modulo.resumo}
                  </p>
                  <Link 
                    to={`/funcionalidades/${modulo.id}`} 
                    className="feature-link"
                    aria-label={`Saiba mais sobre ${modulo.titulo}`}
                  >
                    Saiba mais →
                  </Link>
                </div>
              );
            })}
          </div>
          <div className="features-cta">
            <Link to="/funcionalidades" className="btn btn-primary btn-large">
              Ver Todas as Funcionalidades
            </Link>
          </div>
        </div>
      </section>
            */}
      {/* Video Section */}
      <section id="video" className="video-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Veja o Veritux ERP em Ação</h2>
            <p className="section-subtitle">
              Assista à demonstração completa do sistema
            </p>
          </div>
          <div className="video-container">
            <div className="video-wrapper">
              <iframe
                className="youtube-player"
                src="https://www.youtube.com/embed/LGk9EaiNOQw?si=4VULZEdNYuTxLkPa"
                title="Demonstração do Veritux ERP"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                }}
              />
            </div>
            <div className="video-info">
              <h3 className="video-info-title">O que você vai ver nesta demonstração:</h3>
              <ul className="video-info-list">
                <li>✓ Interface intuitiva e moderna</li>
                <li>✓ Dashboards em tempo real</li>
                <li>✓ Gestão completa de vendas e estoque</li>
                <li>✓ Emissão de NF-e automatizada</li>
                <li>✓ Relatórios e análises avançadas</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section id="integracao" className="integrations-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Integrações que Impulsionam seu Negócio</h2>
            <p className="section-subtitle">
              Conecte o Veritux ERP com as principais plataformas do mercado
            </p>
          </div>
          
          <div className="integrations-grid">
            <div className="integration-category">
              <h3 className="integration-category-title">Fiscal e Logística</h3>
              <div className="integration-items">
                <div className="integration-item">
                  <div className="integration-icon">📄</div>
                  <h4 className="integration-name">Sefaz</h4>
                  <p className="integration-description">Emissão de NF-e e NFC-e</p>
                </div>
                <div className="integration-item">
                  <div className="integration-icon">📦</div>
                  <h4 className="integration-name">Correios</h4>
                  <p className="integration-description">Cálculo de frete e rastreamento</p>
                </div>
                {/*  
                <div className="integration-item">
                  <div className="integration-icon">🚚</div>
                  <h4 className="integration-name">Transportadoras</h4>
                  <p className="integration-description">Integração com principais transportadoras</p>
                </div>
                */}
                <div className="integration-item">
                  <div className="integration-icon">📊</div>
                  <h4 className="integration-name">SPED</h4>
                  <p className="integration-description">Geração automática de obrigações acessórias</p>

                </div>
                
              </div>
            </div>

            <div className="integration-category">
              <h3 className="integration-category-title">E-commerce e Outros</h3>
              <div className="integration-items">
             {/*
                <div className="integration-item">
                  <div className="integration-icon">🛒</div>
                  <h4 className="integration-name">WooCommerce</h4>
                  <p className="integration-description">Integração com lojas virtuais</p>
                </div>
                <div className="integration-item">
                  <div className="integration-icon">📧</div>
                  <h4 className="integration-name">Email Marketing</h4>
                  <p className="integration-description">Integração com principais plataformas</p>
                </div>
                */}
                <div className="integration-item">
                  <div className="integration-icon">☁️</div>
                  <h4 className="integration-name">API REST</h4>
                  <p className="integration-description">Integrações customizadas via API</p>
                </div>
               {/*
                <div className="integration-item">
                  <div className="integration-icon">🔌</div>
                  <h4 className="integration-name">Webhooks</h4>
                  <p className="integration-description">Notificações em tempo real</p>
                </div>
                */}
               
              </div>
            </div>
          </div>
          
          <div className="integrations-cta">
            <p className="integrations-note">
              Precisa de uma integração específica? Nossa equipe pode ajudar!
            </p>
            <a
              href={getWhatsAppUrl('Olá! Gostaria de saber mais sobre integrações do Veritux ERP.')}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline"
              onClick={(e) => {
                trackCTA('whatsapp_integracoes', 'integracoes');
                handleWhatsAppClick(e, 'Olá! Gostaria de saber mais sobre integrações do Veritux ERP.', ADS_EVENTS.WHATSAPP_INTEGRACOES);
              }}
            >
              Falar com especialista no WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="como-funciona" className="how-it-works">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Como Funciona</h2>
            <p className="section-subtitle">
              Simples, rápido e eficiente
            </p>
          </div>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3 className="step-title">Baixe e instale grátis</h3>
              <p className="step-text">
                Baixe o instalador e configure seu acesso em poucos minutos.
                Sem necessidade de cartão de crédito.
              </p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3 className="step-title">Configure seu Negócio</h3>
              <p className="step-text">
                Nossa equipe te ajuda a configurar o sistema 
                de acordo com as necessidades da sua empresa.
              </p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3 className="step-title">Comece a Usar</h3>
              <p className="step-text">
                Em poucos dias você já estará usando todas as funcionalidades 
                e vendo os resultados na prática.
              </p>
            </div>
            <div className="step">
              <div className="step-number">4</div>
              <h3 className="step-title">Cresça com Clareza</h3>
              <p className="step-text">
                Tome decisões baseadas em dados e veja sua empresa 
                crescer de forma previsível e sustentável.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Programa Pioneiro Section */}
      <section className="social-proof">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Programa Pioneiro — 20 vagas</h2>
            <p className="section-subtitle">
              O Veritux está abrindo as primeiras vagas. Seja um dos 20 primeiros clientes,
              use o sistema com condição especial e ajude a construir os próximos cases — reais.
            </p>
          </div>
          <div className="stats">
            <div className="stat-item">
              <div className="stat-number">15</div>
              <div className="stat-label">Dias de teste sem cartão</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">20</div>
              <div className="stat-label">Vagas no Programa Pioneiro</div>
            </div>
            <div className="stat-item">
              <div className="stat-number">1</div>
              <div className="stat-label">Fluxo integrado da operação</div>
            </div>
          </div>
          <p className="stats-footnote">
            Os benefícios reais dependem do uso e do processo de cada empresa. Quando tivermos clientes
            Pioneiros, traremos depoimentos reais e autorizados por escrito.
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="precos" className="pricing" aria-labelledby="precos-title" itemScope itemType="https://schema.org/Product">
        <div className="container">
          <div className="section-header">
            <h2 id="precos-title" className="section-title">Planos que crescem com você</h2>
            <p className="section-subtitle">
              Escolha o plano ideal e a forma de pagamento que cabe no seu bolso
            </p>
          </div>
          <div className="pricing-cycle-selector" role="tablist" aria-label="Tipo de cobrança">
            {opcoesCiclo.map((opcao) => {
              const isSelected = cicloCobranca === opcao.id;
              return (
                <button
                  key={opcao.id}
                  type="button"
                  role="tab"
                  aria-selected={isSelected}
                  aria-controls={`panel-${opcao.id}`}
                  id={`tab-${opcao.id}`}
                  className={`pricing-cycle-option ${isSelected ? 'active' : ''}`}
                  onClick={() => setCicloCobranca(opcao.id)}
                >
                  <span className="pricing-cycle-label">{opcao.label}</span>
                  {opcao.economia != null && (
                    <span className="pricing-cycle-badge">Economize {opcao.economia}%</span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="pricing-grid">
            {planos.map((plano) => {
              const ciclo = plano.precosPorCiclo?.[cicloCobranca];
              const precoExibir = ciclo?.preco ?? ciclo?.precoPorMes ?? plano.preco;
              const totalExibir = ciclo?.total;
              const isPorMes = ciclo?.precoPorMes != null || cicloCobranca === CICLOS_COBRANCA.MENSAL;
              const periodoExibir = isPorMes ? 'mês' : (ciclo?.periodoLabel ?? plano.periodo);
              const economiaPercentual = ciclo?.economiaPercentual;
              const precoOriginal = plano.precoOriginal;
              return (
                <div key={plano.id} className={`pricing-card ${plano.popular ? 'featured' : ''}`}>
                  {plano.badge && (
                    <div className="pricing-badge">{plano.badge}</div>
                  )}
                  <div className="pricing-header">
                    <h3 className="pricing-name">{plano.nome}</h3>
                    <p className="pricing-description">{plano.descricao}</p>
                  </div>
                  {plano.ofertaPioneiro && cicloCobranca === CICLOS_COBRANCA.MENSAL && (
                    <div className="pricing-pioneiro">
                      <span className="pioneiro-seal">★ Oferta Pioneiro — {plano.ofertaPioneiro.vagas} vagas</span>
                      <div className="pioneiro-price">
                        R$ {plano.ofertaPioneiro.precoPromocional.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        <span className="pioneiro-price-period">/mês nos {plano.ofertaPioneiro.mesesPromocao} primeiros meses</span>
                      </div>
                      <p className="pioneiro-after">
                        Depois R$ {plano.ofertaPioneiro.precoAposPromocao.toLocaleString('pt-BR')}/mês, congelado por {plano.ofertaPioneiro.congelamentoMeses} meses
                      </p>
                    </div>
                  )}
                  <div className="pricing-price">
                    {precoOriginal && cicloCobranca === CICLOS_COBRANCA.MENSAL && (
                      <div className="price-original">
                        <span className="price-original-amount">R$ {precoOriginal.toLocaleString('pt-BR')}</span>
                        <span className="price-original-period">/mês</span>
                      </div>
                    )}
                    <div className="price-current">
                      <span className="price-amount">R$ {precoExibir.toLocaleString('pt-BR')}</span>
                      <span className="price-period">/{periodoExibir}</span>
                    </div>
                    {totalExibir != null && cicloCobranca !== CICLOS_COBRANCA.MENSAL && (
                      <div className="price-total-cycle">
                        {cicloCobranca === CICLOS_COBRANCA.TRIMESTRAL
                          ? `R$ ${totalExibir.toLocaleString('pt-BR')} a cada 3 meses`
                          : `R$ ${totalExibir.toLocaleString('pt-BR')} por ano`}
                      </div>
                    )}
                    {economiaPercentual != null && (
                      <div className="price-discount">
                        Economize {economiaPercentual}% em relação ao mensal
                      </div>
                    )}
                    {precoOriginal && cicloCobranca === CICLOS_COBRANCA.MENSAL && (
                      <div className="price-discount">
                        Economize R$ {(precoOriginal - plano.preco).toLocaleString('pt-BR')}/mês
                      </div>
                    )}
                  </div>
                  <ul className="pricing-features">
                    {plano.funcionalidades.map((funcionalidade, index) => (
                      <li key={index}>✓ {funcionalidade}</li>
                    ))}
                  </ul>
                  <a 
                    href="/#demo" 
                    rel="noopener noreferrer" 
                    className={`btn ${plano.popular ? 'btn-primary' : 'btn-outline'} btn-large pricing-btn`}
                    onClick={() => trackCTA('criar_conta_gratis', `precos_${plano.id}`)}
                  >
                    {plano.ofertaPioneiro ? 'Quero ser Pioneiro' : 'Começar com este plano'}
                  </a>
                  {plano.ofertaPioneiro && (
                    <p className="pricing-fineprint">{plano.ofertaPioneiro.textoFino}</p>
                  )}
                </div>
              );
            })}
          </div>
          <div className="pricing-note">
            {beneficiosComuns.map((beneficio, index) => (
              <p key={index}>✓ {beneficio}</p>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Dúvidas Frequentes</h2>
            <p className="section-subtitle">
              Tire suas dúvidas sobre o Veritux ERP
            </p>
          </div>
          <div className="faq-grid">
            <div className="faq-item">
              <h3 className="faq-question">É difícil de usar?</h3>
              <p className="faq-answer">
                Não! Nossa interface é intuitiva e oferecemos treinamento completo incluso. A maioria dos clientes está usando o sistema com confiança em menos de uma semana.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">E se eu já tenho outro sistema?</h3>
              <p className="faq-answer">
                Fazemos migração gratuita dos seus dados. Nossa equipe cuida de toda a transferência, garantindo que nenhuma informação seja perdida.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">Quanto tempo leva para implementar?</h3>
              <p className="faq-answer">
                A implementação básica leva de 3 a 7 dias. Para empresas maiores, pode levar até 2 semanas. Oferecemos suporte dedicado durante todo o processo.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">Preciso de treinamento?</h3>
              <p className="faq-answer">
                Sim, e está incluso! Oferecemos treinamento inicial completo para toda sua equipe, além de documentação detalhada e vídeos tutoriais.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">E se eu quiser cancelar?</h3>
              <p className="faq-answer">
                Você pode cancelar a qualquer momento, sem multa ou taxa. Seus dados ficam disponíveis para exportação por 30 dias após o cancelamento.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">Como o Veritux é instalado?</h3>
              <p className="faq-answer">
                O Veritux é um sistema para Windows, instalado no seu computador. Os dados ficam no seu próprio equipamento, sob o seu controle, e o sistema funciona localmente — sem depender de conexão constante com a internet.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">Meus dados estão seguros?</h3>
              <p className="faq-answer">
                Seus dados ficam armazenados localmente, no seu próprio computador, sob o seu controle. Recomendamos que você mantenha rotinas de backup do equipamento. Não enviamos seus dados para servidores externos.
              </p>
            </div>
            <div className="faq-item">
              <h3 className="faq-question">Posso integrar com outros sistemas?</h3>
              <p className="faq-answer">
                Sim! Oferecemos API REST para integrações customizadas com outros sistemas. Fale com nossa equipe para avaliar o seu cenário e as opções disponíveis.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="demo" className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">
              Pronto para transformar sua gestão?
            </h2>
            <p className="cta-subtitle">
              Preencha o cadastro abaixo para testar o Veritux ERP por 15 dias, sem cartão de crédito.
              Quer falar com a equipe ou instalar no computador? Use os botões abaixo do formulário.
            </p>
            <div id="cnpj-biz-form-container" className="cta-form-container">
              {/* Formulário do cnpj.biz será injetado aqui */}
            </div>
            <div className="cta-buttons">
              <a 
                href={getWhatsAppUrl('Olá! Gostaria de agendar uma demonstração do Veritux ERP.')} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn btn-primary btn-large"
                onClick={(e) => {
                  trackCTA('whatsapp_demo', 'cta_final');
                  handleWhatsAppClick(e, 'Olá! Gostaria de agendar uma demonstração do Veritux ERP.', ADS_EVENTS.WHATSAPP_CTA_FINAL);
                }}
              >
                Agendar conversa no WhatsApp
              </a>
              <a
                href="/downloads"
                rel="noopener noreferrer"
                className="btn btn-secondary btn-large"
                onClick={(e) => {
                  trackCTA('download_instalador', 'cta_final');
                  handleDownloadClick(e, '/downloads', ADS_EVENTS.DOWNLOAD_CTA_FINAL);
                }}
              >
                Baixar instalador e começar a usar
              </a>
            </div>
            <div className="guarantee-badge">
              <h3 className="guarantee-title">Compromissos com você</h3>
              <div className="guarantee-items">
                <p className="guarantee-item">✓ Teste de 15 dias para avaliar com calma, sem cartão</p>
                <p className="guarantee-item">✓ Cancelamento conforme condições do contrato, sem surpresas ocultas</p>
                <p className="guarantee-item">✓ Suporte nos canais e horários informados no atendimento</p>
                <p className="guarantee-item">✓ Configuração e treinamento inicial alinhados ao seu plano</p>
              </div>
            </div>
            <p className="cta-note">
              ✓ Configuração gratuita ✓ Suporte incluso ✓ Cancelamento a qualquer momento
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-main">
            <div className="footer-brand">
              <div className="footer-logo">
                <img src="/Logo.ico" alt="Veritux ERP" className="logo-img" />
                <span className="logo-text">Veritux ERP</span>
              </div>
              <p className="footer-description">
                O ERP que entrega controle total, insights em tempo real 
                e crescimento previsível para sua empresa.
              </p>
            </div>
            
            <div className="footer-links-grid">
              <div className="footer-column">
                <h4 className="footer-title">Produto</h4>
                <ul className="footer-links">
                  <li><a href="/funcionalidades">Funcionalidades</a></li>
                  <li><a href="#precos">Preços</a></li>
                  <li><a href="#demo">Teste Grátis</a></li>
                  <li>
                    <a 
                      href="/downloads"
                      onClick={(e) => handleDownloadClick(e, '/downloads', ADS_EVENTS.DOWNLOAD_FOOTER)}
                    >
                      Downloads
                    </a>
                  </li>
                  <li><a href="#integracao">Integrações</a></li>
                </ul>
              </div>
              
              <div className="footer-column">
                <h4 className="footer-title">Empresa</h4>
                <ul className="footer-links">
                  <li><a href="https://mngd.com.br/">Sobre Nós</a></li>
                  <li>
                    <a 
                      href={getWhatsAppUrl('Olá! Gostaria de entrar em contato com a equipe Veritux ERP.')} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={(e) => handleWhatsAppClick(e, 'Olá! Gostaria de entrar em contato com a equipe Veritux ERP.', ADS_EVENTS.WHATSAPP_FOOTER_CONTATO)}
                    >
                      Contato
                    </a>
                  </li>
                  { /*
                  <li><a href="#blog">Blog</a></li>
                  <li><a href="#carreiras">Carreiras</a></li>
                  */}
                </ul>
              </div>
              
              <div className="footer-column">
                <h4 className="footer-title">Suporte</h4>
                <ul className="footer-links">
                  <li><a href="#faq">Dúvidas frequentes</a></li>
                  <li><a href="/funcionalidades">Funcionalidades e visão geral</a></li>
                  <li>
                    <a
                      href={getWhatsAppUrl('Olá! Preciso de suporte técnico com o Veritux ERP.')}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) =>
                        handleWhatsAppClick(
                          e,
                          'Olá! Preciso de suporte técnico com o Veritux ERP.',
                          ADS_EVENTS.WHATSAPP_FOOTER_CONTATO
                        )
                      }
                    >
                      Suporte no WhatsApp
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="footer-bottom">
            <p className="footer-copyright">
              Desenvolvido por{" "}
              <a
                href="https://mngd.com.br"
                target="_blank"
                rel="noopener noreferrer"
              >
                MNGD Sistemas
              </a>{" "}
              &copy; 2026 Veritux ERP. Todos os direitos reservados.
            </p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Float Button */}
      <WhatsAppFloat />
    </div>
  );
};

export default LandingPage;
